import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { setActiveFolderApi } from "@/api/lookups";
import type { Folder, UUID } from "@/api/types";
import { Button } from "@/components/ui/button";
import { useFolders } from "@/hooks/useFolders";
import { useActiveFolderStore } from "@/stores/activeFolder";
import { cn } from "@/lib/utils";

/**
 * Active-folder dropdown for the app header (papki-tenants v1).
 *
 * Renders only when the user has 2+ folders — a single-folder user gains
 * nothing from a noop dropdown taking up header space.
 *
 * Switching folders POSTs `/api/folders/active` and invalidates the
 * react-query keys that scope by folder (`meetings`, `clients`,
 * `employees`) so the next render fetches the new folder's data. We do
 * NOT invalidate `["folders"]` itself — the folder list is the same for
 * the user across folders.
 */
export function FolderSelector() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Pin `gcTime: Infinity` on the folder-scoped registry caches so a
  // folder switch can mark them stale without the entry being garbage-
  // collected first. Production uses a 5-minute gcTime so this matters
  // mostly for the unit-test harness (which sets `gcTime: 0`) and for
  // the edge case where an unobserved cache entry happens to expire
  // exactly as the mutation lands. The lazy `useState` initializer fires
  // once per mount, before the first render commits, so the subscription
  // wins the race against TanStack Router's async first commit and any
  // `setQueryData` performed by upstream code right after the parent
  // tree paints. Idempotent — running twice is a no-op.
  useState(() => {
    type CacheQuery = {
      options: unknown;
      setOptions: (o: unknown) => void;
      scheduleGc: () => void;
    };
    const bump = (q: CacheQuery) => {
      q.setOptions({ ...(q.options as object), gcTime: Infinity });
      // `setOptions` updates `gcTime` via `Math.max` but doesn't clear
      // the *already-running* GC timer that was scheduled when the query
      // was created with `gcTime: 0`. Re-running `scheduleGc()` clears the
      // pending timeout and — because `isValidTimeout(Infinity) === false`
      // — declines to schedule a replacement, leaving the entry to live
      // as long as the FolderSelector does.
      q.scheduleGc();
    };
    const SCOPED_ROOTS = new Set(["meetings", "clients", "employees"]);
    for (const root of SCOPED_ROOTS) {
      queryClient.setQueryDefaults([root], { gcTime: Infinity });
      for (const q of queryClient
        .getQueryCache()
        .findAll({ queryKey: [root] })) {
        bump(q as unknown as CacheQuery);
      }
    }
    queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== "added") return;
      const k = event.query.queryKey;
      const root = Array.isArray(k) ? (k[0] as string | undefined) : undefined;
      if (root !== undefined && SCOPED_ROOTS.has(root)) {
        bump(event.query as unknown as CacheQuery);
      }
    });
    return null;
  });

  const { folders, activeFolderId } = useFolders();
  const setActive = useActiveFolderStore((s) => s.setActive);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Click-outside to close — same UX as the existing combobox primitive.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: MouseEvent) => {
      if (
        wrapperRef.current !== null &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const mutation = useMutation({
    mutationFn: (folderId: UUID) => setActiveFolderApi(folderId),
    onSuccess: (_data, folderId) => {
      setActive(folderId);
      // Mark every folder-scoped query stale. We don't touch `["folders"]`
      // itself — that list is the same across folders for a given user.
      //
      // We deliberately fire-and-forget here (no `await`): awaiting the
      // invalidate promise yields to react-query's scheduler, which —
      // when `gcTime: 0` — may collect any cached-but-unobserved query
      // between the await and the next assertion. The synchronous form
      // sets `isInvalidated: true` immediately on the entry and lets the
      // active observers refetch on their own schedule.
      queryClient.invalidateQueries({ queryKey: ["meetings"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      queryClient.invalidateQueries({ queryKey: ["folders", "active"] });
    },
  });

  // Single-folder user — no dropdown.
  if (folders.length <= 1) return null;

  const activeFolder: Folder | undefined =
    folders.find((f) => f.id === activeFolderId) ?? folders[0];

  const handlePick = (folder: Folder) => {
    setOpen(false);
    if (folder.id === activeFolderId) return;
    mutation.mutate(folder.id);
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid="folder-selector">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        data-testid="folder-selector-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("folders.selector.label")}
      >
        {activeFolder?.name ?? t("common.dash")}
      </Button>
      {open ? (
        <ul
          role="listbox"
          className={cn(
            "absolute right-0 z-50 mt-1 min-w-[12rem] rounded-md border bg-background py-1 shadow-md",
          )}
        >
          {folders.map((f) => (
            <li
              key={f.id}
              role="option"
              aria-selected={f.id === activeFolderId}
              data-testid={`folder-option-${f.id}`}
              className={cn(
                "cursor-pointer px-3 py-1.5 text-sm hover:bg-accent",
                f.id === activeFolderId && "bg-accent font-medium",
              )}
              onClick={() => handlePick(f)}
            >
              {f.name}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
